import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Disclaimer from "@/components/Disclaimer";
import {
  Sparkles, HeartHandshake, BookOpen, Flame, CalendarCheck, TrendingUp,
  CheckCircle2, Stethoscope, ShoppingBag, Award, ArrowRight, Quote, Bot, Cookie,
} from "lucide-react";

const SHOP_IMG = "https://images.unsplash.com/photo-1760621393386-3906922b0b78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VsbG5lc3MlMjBwcm9kdWN0c3xlbnwwfHx8fDE3ODI1MjAxNzN8MA&ixlib=rb-4.1.0&q=85";

const FOOD_CATS = {
  proteins: ["Chicken", "Fish", "Egg", "Turkey", "Beef"],
  fruits: ["Apple", "Orange", "Pear", "Watermelon", "Kiwi", "Strawberries", "Blueberries", "Raspberries", "Pomegranate", "Banana"],
  veggies: ["Lettuce", "Spinach", "Kale", "Broccoli", "Cabbage", "Peppers", "Asparagus", "Celery", "Tomatoes", "Carrots", "Radish", "Onions", "Mushroom", "Avocado"],
};

const StatCard = ({ icon: Icon, value, label, testid }) => (
  <div data-testid={testid} className="bg-white rounded-3xl p-6 border border-taupe shadow-[0_8px_30px_rgba(107,37,84,0.05)]">
    <Icon className="h-6 w-6 text-wine mb-3" strokeWidth={1.5} />
    <p className="font-serif text-4xl text-[#3A1230]">{value}</p>
    <p className="text-sm text-[#9B7E8D] mt-1">{label}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [faith, setFaith] = useState(null);
  const [todayRitual, setTodayRitual] = useState(null);
  const [foodToday, setFoodToday] = useState({});

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/faith/today").then((r) => setFaith(r.data)).catch(() => {});
    api.get("/ritual/today").then((r) => setTodayRitual(r.data)).catch(() => {});
    api.get(`/shapeup/foodlog?date=${today}`).then((r) => setFoodToday(r.data.counts || {})).catch(() => {});
  }, []);

  const sumCat = (list) => Object.entries(foodToday).reduce((t, [f, c]) => t + (list.includes(f) ? c : 0), 0);
  const food = { proteins: sumCat(FOOD_CATS.proteins), fruits: sumCat(FOOD_CATS.fruits), veggies: sumCat(FOOD_CATS.veggies) };
  const foodTotal = food.proteins + food.fruits + food.veggies;

  const ritualDone = todayRitual && todayRitual.wellness_score != null;
  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  })();

  return (
    <AppLayout>
      <div className="animate-fade-up">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">{greeting}</span>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl tracking-tight text-wine">
          Hello, {user?.name?.split(" ")[0] || "friend"}.
        </h1>
        <p className="mt-3 text-lg text-[#6E5862] max-w-xl">
          Here is your space to check in, be encouraged, and care for yourself today.
        </p>
      </div>

      {/* Stats */}
      <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-streak" icon={Flame} value={stats?.streak ?? "—"} label="Day streak" />
        <StatCard testid="stat-checkins" icon={CalendarCheck} value={stats?.total_check_ins ?? "—"} label="Check-ins" />
        <StatCard testid="stat-score" icon={TrendingUp} value={stats?.avg_score ? `${stats.avg_score}` : "—"} label="Avg wellness" />
        <StatCard testid="stat-prayers" icon={CheckCircle2} value={stats?.prayers_answered ?? "—"} label="Prayers answered" />
      </div>

      {/* Today's nourishment summary */}
      <div data-testid="nourishment-summary" className="mt-8 bg-white rounded-3xl border border-taupe p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-serif text-2xl text-[#3A1230]">Today's nourishment</h3>
            <p className="text-sm text-[#9B7E8D]">
              {foodTotal === 0 ? "Tap foods in ShapeUp's Food Log to start tracking today." : "A snapshot of what you've logged today."}
            </p>
          </div>
          <Link to="/shapeup" data-testid="nourishment-log-link" className="inline-flex items-center gap-2 rounded-full border border-wine px-5 py-2.5 text-sm font-medium text-wine hover:bg-wine/5 transition-all">
            Open Food Log <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-4">
          <div className="rounded-2xl bg-[#F6ECF0] p-5 text-center">
            <p data-testid="summary-proteins" className="font-serif text-4xl text-wine">{food.proteins}</p>
            <p className="text-sm text-[#6E5862] mt-1">proteins</p>
          </div>
          <div className="rounded-2xl bg-[#F6ECF0] p-5 text-center">
            <p data-testid="summary-veggies" className="font-serif text-4xl text-wine">{food.veggies}</p>
            <p className="text-sm text-[#6E5862] mt-1">veggies</p>
          </div>
          <div className="rounded-2xl bg-[#F6ECF0] p-5 text-center">
            <p data-testid="summary-fruits" className="font-serif text-4xl text-wine">{food.fruits}</p>
            <p className="text-sm text-[#6E5862] mt-1">fruits</p>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Link
          to="/ritual"
          data-testid="quick-ritual"
          className="group relative overflow-hidden rounded-3xl bg-wine p-8 text-white transition-all hover:shadow-[0_20px_40px_rgba(107,37,84,0.25)]"
        >
          <Sparkles className="h-8 w-8 mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-3xl">{ritualDone ? "Today's ritual is complete" : "Begin today's ritual"}</h3>
          <p className="mt-2 text-white/80 text-sm max-w-sm">
            {ritualDone ? `Your wellness score today is ${todayRitual.wellness_score}. Revisit anytime.` : "A gentle morning check-in for how you feel, body and soul."}
          </p>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
            {ritualDone ? "View & edit" : "Start now"} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>

        <Link
          to="/faith"
          data-testid="quick-faith"
          className="group relative overflow-hidden rounded-3xl bg-white border border-taupe p-8 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.1)]"
        >
          <Quote className="h-7 w-7 text-gold mb-4" strokeWidth={1.5} />
          {faith?.verse ? (
            <>
              <p className="font-serif italic text-2xl text-[#3A1230] leading-snug">"{faith.verse.text}"</p>
              <p className="mt-3 text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">{faith.verse.ref}</p>
            </>
          ) : (
            <p className="font-serif text-2xl text-[#3A1230]">Your daily scripture awaits.</p>
          )}
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-wine">
            Faith & Prayer <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
      </div>

      {/* Rewards */}
      {stats?.rewards?.length > 0 && (
        <div className="mt-8 bg-[#F7E9EF] rounded-3xl p-6 border border-gold/20">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-gold" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl text-[#3A1230]">Your rewards</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {stats.rewards.map((r) => (
              <span key={r} data-testid="reward-badge" className="inline-flex items-center rounded-full bg-white px-4 py-2 text-sm font-medium text-wine border border-gold/30">
                {r}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ask BloomHer AI */}
      <h2 className="mt-12 font-serif text-3xl text-[#3A1230]">Ask BloomHer</h2>
      <div className="mt-6 grid sm:grid-cols-2 gap-6">
        <Link
          to="/assistant"
          data-testid="cta-ask-ai"
          className="group relative overflow-hidden rounded-3xl bg-wine p-7 text-white transition-all hover:shadow-[0_20px_40px_rgba(107,37,84,0.22)]"
        >
          <Bot className="h-8 w-8 mb-4" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl">Ask BloomHer AI</h3>
          <p className="mt-2 text-sm text-white/80 max-w-xs">Your gentle wellness educator — ask anything about menopause, hormones, sleep & more.</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium">
            Start a chat <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
        <Link
          to={`/assistant?q=${encodeURIComponent("I've been having cravings lately — can you share some gentle, non-judgmental tips to understand and manage them?")}`}
          data-testid="cta-ask-cravings"
          className="group relative overflow-hidden rounded-3xl bg-white border border-taupe p-7 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.1)]"
        >
          <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center mb-4">
            <Cookie className="h-6 w-6 text-wine" strokeWidth={1.5} />
          </div>
          <h3 className="font-serif text-2xl text-[#3A1230]">Ask about your cravings</h3>
          <p className="mt-2 text-sm text-[#9B7E8D] max-w-xs">Get kind, practical guidance on understanding and gently managing cravings.</p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-wine">
            Ask now <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </Link>
      </div>

      {/* Funnel CTAs */}
      <h2 className="mt-12 font-serif text-3xl text-[#3A1230]">Your next steps</h2>
      <div className="mt-6 grid sm:grid-cols-2 gap-6">
        <Link
          to="/care"
          data-testid="cta-book-consult"
          className="group flex items-center gap-5 rounded-3xl bg-white border border-taupe p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.08)]"
        >
          <div className="h-14 w-14 rounded-2xl bg-wine/10 flex items-center justify-center flex-shrink-0">
            <Stethoscope className="h-7 w-7 text-wine" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-serif text-2xl text-[#3A1230]">Book a consult</h3>
            <p className="text-sm text-[#9B7E8D]">Clinical care handled securely by our partner platform.</p>
          </div>
        </Link>
        <a
          href="https://bloomher.health"
          target="_blank"
          rel="noreferrer"
          data-testid="cta-shop-supplements"
          className="group relative flex items-center gap-5 rounded-3xl overflow-hidden border border-taupe p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.08)]"
          style={{ backgroundImage: `linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url(${SHOP_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        >
          <div className="h-14 w-14 rounded-2xl bg-wine/10 flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="h-7 w-7 text-wine" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-serif text-2xl text-[#3A1230]">Shop supplements</h3>
            <p className="text-sm text-[#9B7E8D]">Practitioner-grade options via Fullscript.</p>
          </div>
        </a>
      </div>

      <Link to="/library" data-testid="cta-library" className="mt-6 group flex items-center justify-between rounded-3xl bg-white border border-taupe p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(107,37,84,0.08)]">
        <div className="flex items-center gap-5">
          <div className="h-14 w-14 rounded-2xl bg-wine/10 flex items-center justify-center">
            <BookOpen className="h-7 w-7 text-wine" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-serif text-2xl text-[#3A1230]">Explore the library</h3>
            <p className="text-sm text-[#9B7E8D]">Education on menopause, hormones, sleep, stress & more.</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-wine group-hover:translate-x-1 transition-transform" />
      </Link>

      <Disclaimer className="mt-10" />
    </AppLayout>
  );
}
